import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate, useBlocker } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { useForm, FormProvider } from "react-hook-form";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import ToastNotification, {
  ToastType,
} from "components/common/ToastNotification";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import PermissoesManager from "../editarGrupo/PermissoesManager";
import DeletedBorderCard from "components/common/DeletedBorderCard";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useInvalidateMenuCache } from "hooks/useMenu";
import { useToast } from "providers/ToastProvider";

// Define breadcrumbs for the new group page
const grupoBreadcrumbItems = [
  {
    label: "Cadastros",
    url: "#!",
  },
  {
    label: "Grupos de Acesso",
    url: "/cadastros/grupos",
  },
  {
    label: "Novo",
    active: true,
  },
];

// Define the types for the form and API responses
interface GrupoFormValues {
  descricao: string;
  permissoes: number[];
}


const NovoGrupo = () => {
  const navigate = useNavigate();
  const invalidateMenuCache = useInvalidateMenuCache();

  const { showToast } = useToast();

  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );

  // Use the permission modal hook
  const { getMutationErrorHandler } = usePermissionModal();

  const formMethods = useForm<GrupoFormValues>({
    defaultValues: {
      descricao: "",
      permissoes: [],
    },
  });

  const isDirtyRef = useRef(false);

  // Effect to update the dirty ref whenever form state changes
  useEffect(() => {
    isDirtyRef.current = formMethods.formState.isDirty;
  }, [formMethods.formState.isDirty]);

  const createGrupoMutation = useMutation({
    mutationFn: async (formData: GrupoFormValues) => {
      const response = await axios.post("/api/grupo", formData);
      return response.data;
    },
    onSuccess: (response) => {
      // Reset dirty status
      formMethods.reset();
      isDirtyRef.current = false;

      // Invalidar o cache de menu para refletir possíveis mudanças nas permissões
      invalidateMenuCache();

      showToast(<>Grupo foi criado com sucesso!</>, "success");

      // Navigate back to grupos list with success state
      navigate("/cadastros/grupos");
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para cadastrar grupos de acesso.",
      (error) => {
        showToast(error.response?.data?.message || "Erro ao criar grupo", "error");
      }
    ),
  });

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDirtyRef.current) return false;
    if (formMethods.formState.isSubmitting) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    setPendingNavigation(nextLocation.pathname);
    setShowNavigationModal(true);

    return true;
  });

  // Function to handle navigation confirmation
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      isDirtyRef.current = false;
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  // Function to cancel navigation
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    blocker.reset?.();
  };

  const handleSubmit = async (formData: GrupoFormValues) => {
    try {
      await createGrupoMutation.mutate(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div>
      <UnsavedChangesModal
        show={showNavigationModal}
        onConfirm={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />

      <PageBreadcrumb items={grupoBreadcrumbItems} />

      <FormProvider {...formMethods}>
        <Form
          noValidate
          className="mb-9"
          onSubmit={formMethods.handleSubmit(handleSubmit)}
        >
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="mb-2">Cadastrar um Grupo de Acesso</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações do novo grupo
              </h5>
            </div>
            <div>
              <Button
                variant="primary"
                type="submit"
                className="w-100"
                disabled={createGrupoMutation.isPending}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {createGrupoMutation.isPending ? "Salvando..." : "Salvar Grupo"}
              </Button>
            </div>
          </div>

          {/* Informações do Grupo */}
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h5 className="mb-3">Informações do Grupo</h5>
              <div className="row g-3">
                <div className="col-12 mb-3">
                  <Form.Group controlId="descricao">
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Digite o nome do grupo"
                      {...formMethods.register("descricao", {
                        required: "Nome do grupo é obrigatório",
                      })}
                      isInvalid={!!formMethods.formState.errors.descricao}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formMethods.formState.errors.descricao?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </Card>

          <DeletedBorderCard>
            <PermissoesManager />
          </DeletedBorderCard>

          <div className="d-flex flex-column flex-md-row justify-content-between mt-4 gap-3 gap-md-0">
            <Button
              variant="secondary"
              type="button"
              className="order-2 order-md-1"
              onClick={() => navigate("/cadastros/grupos")}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="order-1 order-md-2"
              disabled={createGrupoMutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {createGrupoMutation.isPending ? "Salvando..." : "Salvar Grupo"}
            </Button>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovoGrupo;
